import React, { ChangeEvent, forwardRef, MouseEvent } from 'react';
import { TextInput } from '../TextField/TextInput';
import { InputAdornment, SxProps, TextFieldProps, Theme } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { styles } from '~/components/FormComponents/styles.ts';

interface IDateInputField {
  dateInputValue?: string;
  disabled?: boolean;
  onClick: (e: MouseEvent<HTMLInputElement>) => void;
  handleInputBlur: () => void;
  handleInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  hasError?: boolean;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  dataTestId?: string;
  inputRef?: React.Ref<unknown>;
  variant?: TextFieldProps['variant'];
  sx?: SxProps<Theme>;
}
export const DateInputField = forwardRef<HTMLInputElement, IDateInputField>(
  (
    {
      dateInputValue,
      disabled,
      onClick,
      handleInputBlur,
      handleInputChange,
      error,
      helperText,
      label,
      placeholder,
      inputRef,
      variant,
      sx,
      dataTestId,
    },
    ref
  ) => {
    const assignRef = <T,>(targetRef: React.Ref<T> | undefined, value: T | null) => {
      if (typeof targetRef === 'function') {
        targetRef(value);
        return;
      }

      if (targetRef) {
        (targetRef as React.MutableRefObject<T | null>).current = value;
      }
    };
    const handleInputRef = (instance: HTMLInputElement | null) => {
      assignRef(ref, instance);
      assignRef(inputRef, instance);
    };

    return (
      <TextInput
        value={dateInputValue}
        data-testid={dataTestId}
        sx={{ ...sx, zIndex: 0, cursor: disabled ? 'not-allowed' : '' }}
        inputRef={handleInputRef}
        onClick={!disabled ? onClick : undefined}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
        placeholder={placeholder}
        variant={variant}
        slotProps={{
          htmlInput: {
            maxLength: 10,
            'data-testid': dataTestId ? `${dataTestId}-input` : 'datePicker-input',
          },
          formHelperText: {
            'data-testid': dataTestId ? `${dataTestId}-error` : 'datePicker-error',
          } as React.HTMLAttributes<HTMLParagraphElement>,
          input: {
            endAdornment: (
              <InputAdornment position="end" sx={styles.textContainer}>
                <CalendarMonth />
              </InputAdornment>
            ),
          },
        }}
        label={label}
        fullWidth
        error={error}
        helperText={helperText}
        disabled={disabled}
      />
    );
  }
);
